import { useCallback, useEffect, useRef } from "react";

const DEFAULT_DEBOUNCE_MS = 700;

/**
 * Coalesces save requests into a single debounced flush.
 * onBlur / field edits enqueue; a backup manual save can flush immediately.
 */
export function useAutosaveQueue({
  onFlush,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  enabled = true,
}) {
  const pendingRef = useRef(false);
  const timerRef = useRef(null);
  const flushingRef = useRef(false);
  const queuedDuringFlushRef = useRef(false);
  const onFlushRef = useRef(onFlush);

  useEffect(() => {
    onFlushRef.current = onFlush;
  }, [onFlush]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const runFlush = useCallback(async () => {
    if (!enabled) return;
    if (flushingRef.current) {
      queuedDuringFlushRef.current = true;
      return;
    }

    pendingRef.current = false;
    flushingRef.current = true;

    try {
      await onFlushRef.current?.();
    } finally {
      flushingRef.current = false;

      if (queuedDuringFlushRef.current) {
        queuedDuringFlushRef.current = false;
        pendingRef.current = true;
        timerRef.current = setTimeout(() => {
          runFlush();
        }, debounceMs);
      }
    }
  }, [debounceMs, enabled]);

  const enqueue = useCallback(() => {
    if (!enabled) return;

    pendingRef.current = true;

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      runFlush();
    }, debounceMs);
  }, [debounceMs, enabled, runFlush]);

  const flushNow = useCallback(async () => {
    if (!enabled) return;

    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    pendingRef.current = true;
    await runFlush();
  }, [enabled, runFlush]);

  const cancel = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    pendingRef.current = false;
    queuedDuringFlushRef.current = false;
  }, []);

  return {
    enqueue,
    flushNow,
    cancel,
    hasPending: () => pendingRef.current || flushingRef.current,
  };
}
